<template>
  <div v-if="loading" class="flex justify-center py-20">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
  </div>

  <div v-else-if="error" class="text-center py-20">
    <p class="text-red-500">{{ error }}</p>
  </div>

  <div v-else>
    <!-- Status Banner -->
    <div :class="bannerClass" class="rounded-xl px-4 py-3 mb-6 text-center text-sm font-medium">
      {{ bannerText }}
    </div>

    <!-- GBP Post Preview Card -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
      <!-- Business header (GBP-style) -->
      <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
        <div class="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold text-sm">
          {{ businessInitial }}
        </div>
        <div>
          <p class="font-semibold text-sm text-gray-900">{{ business.name }}</p>
          <p class="text-xs text-gray-500">{{ post.scheduled_date || 'Not scheduled' }}</p>
        </div>
      </div>

      <!-- Post Image -->
      <div v-if="post.image_url">
        <img :src="post.image_url" class="w-full" alt="Post image">
      </div>

      <!-- Post Body -->
      <div class="px-4 py-3">
        <h3 v-if="post.title" class="font-semibold text-gray-900 mb-1">{{ post.title }}</h3>
        <p class="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{{ post.body }}</p>
      </div>

      <!-- CTA Button (GBP-style) -->
      <div v-if="post.cta_type" class="px-4 pb-4">
        <div class="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-full">
          <svg v-if="post.cta_type === 'call_now'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {{ post.cta_type === 'call_now' ? 'Call now' : 'Learn more' }}
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div v-if="canAct" class="flex gap-3 mb-6">
      <button @click="handleApprove" :disabled="submitting"
        class="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-medium py-3 px-4 rounded-xl text-sm transition-colors">
        {{ submitting ? 'Submitting...' : 'Approve' }}
      </button>
      <button @click="showChangesForm = !showChangesForm" :disabled="submitting"
        class="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-medium py-3 px-4 rounded-xl text-sm transition-colors">
        Request Changes
      </button>
    </div>

    <!-- Request Changes Form -->
    <div v-if="showChangesForm && canAct" class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
      <label class="block text-sm font-medium text-gray-700 mb-2">What would you like changed?</label>
      <textarea v-model="changesComment" rows="3"
        class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        placeholder="Describe the changes you'd like..."></textarea>
      <div class="flex items-center gap-3 mt-3">
        <input v-model="authorName" type="text"
          class="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Your name (optional)">
        <button @click="handleRequestChanges" :disabled="!changesComment.trim() || submitting"
          class="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg text-sm transition-colors">
          Submit
        </button>
      </div>
    </div>

    <!-- Comment Thread -->
    <div v-if="comments.length > 0" class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div class="px-4 py-3 border-b border-gray-100">
        <h4 class="text-sm font-semibold text-gray-700">Comments ({{ comments.length }})</h4>
      </div>
      <div class="divide-y divide-gray-100 max-h-96 overflow-y-auto">
        <div v-for="comment in comments" :key="comment.id"
          :class="commentClass(comment)" class="px-4 py-3">
          <div class="flex justify-between items-start mb-1">
            <span class="text-sm font-medium" :class="comment.author_type === 'client' ? 'text-amber-700' : 'text-primary-700'">
              {{ comment.author_name }}
            </span>
            <span class="text-xs text-gray-400">{{ formatTime(comment.created_at) }}</span>
          </div>
          <p class="text-sm text-gray-700">{{ comment.body }}</p>
        </div>
      </div>

      <!-- Add comment -->
      <div v-if="approval.status !== 'approved'" class="border-t border-gray-100 p-4">
        <div class="flex gap-2">
          <input v-model="newComment" type="text"
            class="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Add a comment..." @keyup.enter="handleAddComment">
          <button @click="handleAddComment" :disabled="!newComment.trim()"
            class="bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg text-sm transition-colors">
            Send
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getApprovalData, approvePost, requestChanges, addComment } from '~/javascript/api/approval_reviews'

const props = defineProps({ token: { type: String, required: true } })

const loading = ref(true)
const error = ref(null)
const submitting = ref(false)
const showChangesForm = ref(false)
const changesComment = ref('')
const authorName = ref('')
const newComment = ref('')

const post = ref({})
const business = ref({})
const approval = ref({})
const comments = ref([])

const businessInitial = computed(() => (business.value.name || '?')[0].toUpperCase())

const canAct = computed(() => ['pending', 'revised'].includes(approval.value.status))

const bannerConfig = {
  pending: { class: 'bg-blue-50 text-blue-800 border border-blue-200', text: 'Awaiting your review' },
  approved: { class: 'bg-green-50 text-green-800 border border-green-200', text: 'Approved' },
  changes_requested: { class: 'bg-amber-50 text-amber-800 border border-amber-200', text: 'Changes requested' },
  revised: { class: 'bg-blue-50 text-blue-800 border border-blue-200', text: 'Revised — please review again' }
}

const bannerClass = computed(() => bannerConfig[approval.value.status]?.class || '')
const bannerText = computed(() => bannerConfig[approval.value.status]?.text || '')

function commentClass(comment) {
  if (comment.author_type === 'client') return 'bg-amber-50/50'
  return ''
}

function formatTime(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
}

async function fetchData() {
  try {
    const { data } = await getApprovalData(props.token)
    post.value = data.post
    business.value = data.business
    approval.value = data.approval
    comments.value = data.comments
  } catch (e) {
    error.value = 'Unable to load review. The link may have expired.'
  } finally {
    loading.value = false
  }
}

async function handleApprove() {
  submitting.value = true
  try {
    await approvePost(props.token, authorName.value)
    approval.value.status = 'approved'
    approval.value.approved_at = new Date().toISOString()
    await fetchData()
  } catch (e) {
    error.value = 'Failed to approve. Please try again.'
  } finally {
    submitting.value = false
  }
}

async function handleRequestChanges() {
  submitting.value = true
  try {
    const { data } = await requestChanges(props.token, changesComment.value, authorName.value || 'Client')
    approval.value.status = 'changes_requested'
    comments.value = data.comments
    changesComment.value = ''
    showChangesForm.value = false
  } catch (e) {
    error.value = 'Failed to submit. Please try again.'
  } finally {
    submitting.value = false
  }
}

async function handleAddComment() {
  if (!newComment.value.trim()) return
  try {
    const { data } = await addComment(props.token, newComment.value, authorName.value || approval.value.client_name || 'Client')
    comments.value.push(data)
    newComment.value = ''
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to add comment:', e)
  }
}

onMounted(fetchData)
</script>
